import React, { Suspense, lazy, useEffect, useMemo, useRef, useState } from 'react';
import { message } from 'antd';
import { Link } from 'react-router-dom';
import api from '../../../system/client/src/services/api';
import PageShell from '../../Components/PageShell';
import TradersTable from '../Traders/Components/TradersTable';
import CommodityFilter from '../Traders/Components/CommodityFilter';
import { validatePostcode, canCentreRadius } from '../Traders/postcodeInput';
import useHints from '../../Components/useHints';
import SizeFilter from '../Traders/Components/SizeFilter';
import { matchesSize, offeredSizes } from '../Traders/companySize';
import CustomsFilter from '../Traders/Components/CustomsFilter';
import BusinessTypeFilter from '../Traders/Components/BusinessTypeFilter';
import { matchesType, offeredTypes } from '../Traders/businessType';
import { matchesTreatments, offeredTreatments, useCustomsEnabled } from '../Traders/customsTreatments';
import '../Traders/TradersPage.css';
import './NewToTradePage.css';

const TradersMap = lazy(() => import('../Traders/Components/TradersMap'));

/* New to trade: companies that started importing or exporting in the newest
 * HMRC month (or the last three). A company that has just started importing
 * is the best lead a forwarder can get — it probably has no forwarder yet.
 * Same list/map, sort and quick view as the Traders page. */

const STORAGE = {
    FILTERS: 'new_to_trade_filters',
    SORT: 'new_to_trade_sort',
    VIEW: 'traders_view', // shared with the Traders page on purpose
};
const RADIUS_OPTIONS = [5, 10, 15, 20, 25];
const DEFAULT_RADIUS = 25;

const load = (key, fallback) => {
    try {
        const raw = localStorage.getItem(key);
        return raw ? JSON.parse(raw) : fallback;
    } catch {
        return fallback;
    }
};
const save = (key, value) => {
    try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* ignore */ }
};

// What "Clear filters" goes back to: new importers, latest month, whole UK.
const DEFAULT_FILTERS = {
    direction: 'import',
    monthsBack: 1,
    postCode: '',
    radiusMiles: '',
    hs2Codes: [],
    sizeBands: [],
    businessTypes: [],
    customsTreatments: [],
};

const monthName = (months, id) => months.find((m) => m.id === id)?.label || String(id);

const NewToTradePage = () => {
    const customsOn = useCustomsEnabled(); // org setting — hides the border filter when off
    const hints = useHints();
    const [filters, setFilters] = useState(() => {
        // Start from the area last searched on the Traders page, if any.
        const traders = load('traders_filters', {});
        const stored = {
            direction: 'import',
            monthsBack: 1,
            postCode: traders.postCode || '',
            radiusMiles: traders.radiusMiles || '',
            hs2Codes: [],
            ...load(STORAGE.FILTERS, {}),
        };
        return { ...stored, sizeBands: offeredSizes(stored.sizeBands), businessTypes: offeredTypes(stored.businessTypes), customsTreatments: offeredTreatments(stored.customsTreatments) };
    });
    const [canRadius, setCanRadius] = useState(false);
    const [rows, setRows] = useState([]);
    const [months, setMonths] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searched, setSearched] = useState(null); // the filters of the last run
    const [sortBy, setSortBy] = useState(() => load(STORAGE.SORT, 'import-customs-desc'));
    const [view, setView] = useState(() => (load(STORAGE.VIEW, 'list') === 'map' ? 'map' : 'list'));
    const [commodityLists, setCommodityLists] = useState([]);
    const [mapError, setMapError] = useState(null);
    const requestRef = useRef(0);
    const postcodeError = validatePostcode(filters.postCode);
    // Anything set beyond the defaults? (Enables "Clear filters".)
    const hasFilters = Boolean(filters.postCode || filters.radiusMiles || filters.hs2Codes?.length
        || filters.sizeBands?.length || filters.businessTypes?.length || (customsOn && filters.customsTreatments?.length)
        || filters.direction !== 'import' || filters.monthsBack !== 1);
    const currentFilters = filters;

    useEffect(() => save(STORAGE.FILTERS, filters), [filters]);
    useEffect(() => save(STORAGE.SORT, sortBy), [sortBy]);
    useEffect(() => save(STORAGE.VIEW, view), [view]);
    // Visiting the page is what the nav hint was asking for.
    useEffect(() => { hints.dismiss('new-to-trade'); }, [hints]);

    /* Trial and paid plans only. For them, show results straight away — the
     * last-used area, else the whole UK. Free plans see what the page does
     * and an upgrade button instead (plan: null while we find out). */
    const [plan, setPlan] = useState(null); // { allowed, radius, mapArea }
    const [teaser, setTeaser] = useState(null); // free plan: this month's count
    const autoRan = useRef(false);
    useEffect(() => {
        api.get('subscription')
            .then((sub) => ({
                allowed: Boolean(sub?.adminBypass || sub?.limits?.newToTrade),
                radius: Boolean(sub?.adminBypass || sub?.limits?.radiusSearch),
                mapArea: Boolean(sub?.adminBypass || sub?.limits?.mapAreaSearch),
            }))
            .catch(() => ({ allowed: false, radius: false, mapArea: false }))
            .then((p) => {
                setPlan(p);
                setCanRadius(p.radius);
                if (!p.allowed) {
                    // Counts only (free, no names) for the upgrade message.
                    const params = filters.postCode ? { postCode: filters.postCode, radiusMiles: filters.radiusMiles || undefined } : {};
                    api.get('traders/new/summary', { params }).then((s) => {
                        if (!s?.total) return;
                        const where = s.area
                            ? (s.area.radiusMiles ? `within ${s.area.radiusMiles} miles of ${s.area.postCode.toUpperCase()}` : `in ${s.area.postCode.toUpperCase()}`)
                            : 'across the UK';
                        setTeaser({ importers: s.importers, where, month: s.month.label });
                    }).catch(() => {});
                }
                if (autoRan.current || !p.allowed) return;
                autoRan.current = true;
                run({ using: filters });
            });
        api.get('commodity-lists').then((d) => setCommodityLists(d || [])).catch(() => {});
    }, []);

    const set = (field) => (value) => setFilters((f) => {
        const next = { ...f, [field]: value };
        if (field === 'postCode') {
            if (!canCentreRadius(value)) next.radiusMiles = '';
            else if (canRadius && !f.radiusMiles) next.radiusMiles = DEFAULT_RADIUS;
        }
        return next;
    });

    const run = async ({ bounds, using } = {}) => {
        const filters = using || currentFilters;
        if (validatePostcode(filters.postCode)) return;
        const requestId = ++requestRef.current;
        setLoading(true);
        setMapError(null);
        try {
            const params = {
                direction: filters.direction,
                monthsBack: filters.monthsBack,
            };
            if (bounds) {
                params.bounds = [bounds.south, bounds.west, bounds.north, bounds.east].map((v) => v.toFixed(5)).join(',');
            } else if (filters.postCode) {
                params.postCode = filters.postCode;
                if (Number(filters.radiusMiles) > 0) params.radiusMiles = filters.radiusMiles;
            }
            const data = await api.get('traders/new', { params });
            if (requestId !== requestRef.current) return;
            setRows(data?.rows || []);
            setMonths(data?.months || []);
            setSearched({ ...filters, fromMap: Boolean(bounds) });
        } catch (e) {
            if (requestId !== requestRef.current) return;
            if (bounds) setMapError(e?.response?.data?.error || 'Could not load this area');
            else if (e?.response?.status !== 402) message.error('Could not load new companies');
        } finally {
            if (requestId === requestRef.current) setLoading(false);
        }
    };

    // Commodity filter runs on the loaded rows, like the Traders page's refine.
    const shown = useMemo(() => rows.filter((t) => {
        if (!matchesSize(t, filters.sizeBands)) return false;
        if (!matchesType(t, filters.businessTypes)) return false;
        if (customsOn && !matchesTreatments(t, filters.customsTreatments)) return false;
        if (!filters.hs2Codes?.length) return true;
        return (t.trades || []).some(
            (tr) => filters.hs2Codes.includes(tr.hs2_code) || filters.hs2Codes.includes(tr.hs4_code),
        );
    }), [rows, filters.hs2Codes, filters.sizeBands, filters.businessTypes, filters.customsTreatments, customsOn]);

    const direction = searched?.direction || filters.direction;
    const tag = (t) => {
        const bits = [];
        if (t.new_import_month && direction !== 'export') bits.push(`Started importing ${monthName(months, t.new_import_month)}`);
        if (t.new_export_month && direction !== 'import') bits.push(`Started exporting ${monthName(months, t.new_export_month)}`);
        return (
            <span className="ntt-tag">
                {t.brand_new && <span className="ntt-first">First ever trade</span>}
                {bits.join(' · ')}
            </span>
        );
    };

    const whenText = months.length > 1
        ? `${months[months.length - 1].label} – ${months[0].label}`
        : months[0]?.label;
    const areaText = searched?.fromMap
        ? 'in this map area'
        : searched?.postCode
            ? `${Number(searched.radiusMiles) > 0 ? `within ${searched.radiusMiles} miles of` : 'in'} ${searched.postCode.toUpperCase()}`
            : 'across the UK';
    const verb = direction === 'import' ? 'importers' : direction === 'export' ? 'exporters' : 'traders';

    const sidebar = (
        <>
            <div className="dash-side-scroll">
                <div className="group">
                    <span className="group-label">Show</span>
                    <div className="filter-group">
                        <div className="form-field">
                            <label>Companies that started</label>
                            <div className="ntt-segment" role="group" aria-label="Companies that started">
                                {[
                                    { key: 'import', label: 'Importing' },
                                    { key: 'export', label: 'Exporting' },
                                    { key: 'any', label: 'Either' },
                                ].map((o) => (
                                    <button key={o.key} type="button"
                                        className={filters.direction === o.key ? 'on' : ''}
                                        onClick={() => set('direction')(o.key)}>
                                        {o.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="form-field">
                            <label>When</label>
                            <div className="ntt-segment" role="group" aria-label="When">
                                {[
                                    { key: 1, label: 'Latest month' },
                                    { key: 3, label: 'Last 3 months' },
                                ].map((o) => (
                                    <button key={o.key} type="button"
                                        className={filters.monthsBack === o.key ? 'on' : ''}
                                        onClick={() => set('monthsBack')(o.key)}>
                                        {o.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="group">
                    <span className="group-label">Where</span>
                    <div className="filter-group">
                        <div className="form-field">
                            <label htmlFor="ntt-postcode">Postcode or town</label>
                            <input id="ntt-postcode" type="text" placeholder="Leave empty for the whole UK"
                                value={filters.postCode}
                                onChange={(e) => set('postCode')(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && run()} />
                            {postcodeError && <span className="form-error">{postcodeError}</span>}
                        </div>
                        <div className="form-field">
                            <label htmlFor="ntt-radius">Radius</label>
                            <select id="ntt-radius" value={filters.radiusMiles || ''}
                                disabled={!canCentreRadius(filters.postCode)}
                                onChange={(e) => set('radiusMiles')(e.target.value ? Number(e.target.value) : '')}>
                                <option value="">Off</option>
                                {RADIUS_OPTIONS.map((m) => <option key={m} value={m}>{m} miles</option>)}
                            </select>
                        </div>
                        <div className="form-field">
                            <label>Company size</label>
                            <SizeFilter value={filters.sizeBands || []} onChange={set('sizeBands')} />
                        </div>
                        <div className="form-field">
                            <label title="From the company's Companies House activity codes">Business type</label>
                            <BusinessTypeFilter value={filters.businessTypes || []} onChange={set('businessTypes')} />
                        </div>
                    </div>
                </div>

                <div className="group">
                    <span className="group-label">Refine</span>
                    <div className="filter-group">
                        <div className="form-field">
                            <label>Commodity</label>
                            <CommodityFilter value={filters.hs2Codes} onChange={set('hs2Codes')}
                                placeholder="Any goods…" variant="accent" quickLists={commodityLists} />
                        </div>
                        {customsOn && (
                            <div className="form-field">
                                <label title="Companies whose goods might need any of these at the border, from the HMRC tariff">
                                    Might need at the border
                                </label>
                                <CustomsFilter value={filters.customsTreatments || []} onChange={set('customsTreatments')} />
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div className="dash-side-actions">
                <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                        setFilters(DEFAULT_FILTERS);
                        run({ using: DEFAULT_FILTERS });
                    }}
                    disabled={loading || !hasFilters}
                >
                    Clear filters
                </button>
                <button type="button" className="btn btn-primary" onClick={() => run()}
                    disabled={loading || Boolean(postcodeError)}>
                    {loading ? 'Loading…' : 'Update results'}
                </button>
            </div>
        </>
    );

    const viewTabs = (
        <nav className="tab-bar results-view-tabs" aria-label="Results view">
            {[{ key: 'list', label: 'List' }, { key: 'map', label: 'Map' }].map((t) => (
                <button key={t.key} type="button" className={`tab ${view === t.key ? 'on' : ''}`}
                    onClick={() => setView(t.key)}>
                    {t.label}
                </button>
            ))}
        </nav>
    );

    // Free plan: say what's here (with this month's real count) and offer the upgrade.
    if (plan && !plan.allowed) {
        return (
            <PageShell>
                <div className="results-bar">
                    <div className="results-bar-text">
                        <h1>New to trade</h1>
                        <span className="results-bar-meta">Companies that have just started importing or exporting</span>
                    </div>
                </div>
                <div className="empty ntt-locked">
                    <span className="kicker">Included when you subscribe</span>
                    {teaser ? (
                        <p className="ntt-locked-lead">
                            <strong>{teaser.importers.toLocaleString()}</strong> companies {teaser.where} started
                            importing in {teaser.month}.
                        </p>
                    ) : null}
                    <p>
                        Every month, see the companies that traded for the first time, by area and by the goods they
                        move. A business that has just started importing probably doesn&apos;t have a forwarder yet.
                    </p>
                    <Link to="/subscription" className="btn btn-primary">Upgrade to see who they are</Link>
                </div>
            </PageShell>
        );
    }

    return (
        <PageShell sidebar={sidebar}>
            <div className="results-bar">
                <div className="results-bar-text">
                    <h1>New to trade</h1>
                    <span className="results-bar-meta">
                        {searched
                            ? `${shown.length.toLocaleString()} new ${verb} ${areaText}${whenText ? ` · ${whenText}` : ''}`
                            : 'Loading companies that have just started trading…'}
                    </span>
                </div>
                {viewTabs}
            </div>

            {view === 'map' && (shown.length > 0 || searched?.fromMap) ? (
                <Suspense fallback={<div className="fs-map-loading">Loading map…</div>}>
                    <TradersMap
                        traders={shown}
                        resultsKey={searched}
                        searching={loading}
                        canLoadArea={Boolean(plan?.mapArea)}
                        onAreaChange={(bounds) => run({ bounds })}
                        areaError={mapError}
                    />
                </Suspense>
            ) : (
                <TradersTable
                    traders={shown}
                    allCount={rows.length}
                    loading={loading}
                    sortBy={sortBy}
                    onSortChange={setSortBy}
                    hasSearched={Boolean(searched)}
                    renderTag={tag}
                />
            )}

            <p className="data-attribution">
                Contains HMRC data &copy; Crown copyright
                {view === 'map' && ' · Contains OS data © Crown copyright and database right'}
            </p>
        </PageShell>
    );
};

export default NewToTradePage;
